import React, { useMemo, useState } from 'react';
import CascaDeModal from './shared/CascaDeModal';
import AvisoDeCotasDivergentes from './AvisoDeCotasDivergentes';
import { rateioService } from '../services/api';
import { esquecerRateio } from '../hooks/useRateio';
import { ComRateio, TEXTO_DO_CRITERIO } from '../utils/rateioDoGrupo';
import { CotasDivergentes, lerCotasDivergentes } from '../utils/cotasDivergentes';
import { CriterioDeRateio, ProblemaDaDeclaracao, lerProblemasDaDeclaracao } from '../utils/motorDoRateio';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';

/**
 * DECLARAR COMPETÊNCIA PASSADA (backend #301, `POST .../rateio/competencia-passada`).
 *
 * Para um mês que já passou, a divisão é DECLARADA, não medida: a rota normal
 * mediria o pool de HOJE e gravaria a divisão de hoje com rótulo de ontem. O
 * formulário nasce com a versão vigente como ponto de partida; quem declara ajusta
 * para os números daquele mês e diz de onde saíram (observação obrigatória).
 *
 * Erros: o 400 `declaracao_invalida` lista TODOS os problemas de uma vez; o 409 de
 * cotas divergentes usa o `AvisoDeCotasDivergentes` (#242), com o recalcular nunca
 * marcado por padrão.
 */

const paraNumero = (s: string) => {
  const t = String(s || '').trim().replace(/\./g, '').replace(',', '.');
  return t === '' ? NaN : Number(t);
};
const paraTexto = (n: number | null | undefined) =>
  n == null || Number.isNaN(Number(n)) ? '' : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const cents = (n: number) => Math.round(n * 100);

interface LinhaDoForm { clientId: number; percentual: string; valor: string; nfse: string; nfcom: string }

interface Props {
  rateio: ComRateio;
  nome: (clientId: number) => string;
  onClose: () => void;
  onDeclarado: () => void;
}

export default function DeclararCompetenciaPassada({ rateio, nome, onClose, onDeclarado }: Props) {
  const [competencia, setCompetencia] = useState('');
  const [criterio, setCriterio] = useState<CriterioDeRateio>(rateio.criterio as CriterioDeRateio);
  const [pool, setPool] = useState(paraTexto(rateio.pool));
  const [observacao, setObservacao] = useState('');
  const [linhas, setLinhas] = useState<LinhaDoForm[]>(() =>
    rateio.linhas.map((l: any) => ({
      clientId: l.clientId,
      percentual: paraTexto(l.percentualVigente),
      // Pré-preenche com o ACORDADO da versão; sem ele, com o que se cobra hoje.
      valor: paraTexto(l.valorAcordado ?? l.valorCobrado),
      nfse: paraTexto(l.valorVigentePorNatureza?.nfse),
      nfcom: paraTexto(l.valorVigentePorNatureza?.nfcom),
    })),
  );
  const [problemas, setProblemas] = useState<ProblemaDaDeclaracao[] | null>(null);
  const [erroSimples, setErroSimples] = useState<string | null>(null);
  const [divergencia, setDivergencia] = useState<CotasDivergentes | null>(null);
  const [recalcular, setRecalcular] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const soma = useMemo(() => {
    const valor = linhas.reduce((s, l) => s + (Number.isNaN(paraNumero(l.valor)) ? 0 : cents(paraNumero(l.valor))), 0);
    const pct = linhas.reduce((s, l) => s + (Number.isNaN(paraNumero(l.percentual)) ? 0 : paraNumero(l.percentual)), 0);
    return { valor, pct };
  }, [linhas]);
  const poolCentavos = Number.isNaN(paraNumero(pool)) ? null : cents(paraNumero(pool));

  const mudar = (i: number, campo: keyof LinhaDoForm, v: string) =>
    setLinhas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: v } : l)));

  const declarar = async () => {
    setEnviando(true);
    setProblemas(null);
    setErroSimples(null);
    try {
      await rateioService.declararCompetenciaPassada(rateio.matrizId, {
        competenciaInicio: competencia,
        criterio,
        produtoIds: rateio.produtoIds,
        produtoIdsBase: rateio.produtoIdsBase,
        baseDoPercentual: rateio.baseDoPercentual,
        descontosSeguemRateio: rateio.descontosSeguemRateio,
        poolDaCompetencia: paraNumero(pool),
        observacao: observacao.trim(),
        fatias: linhas.map((l) => {
          const nfse = paraNumero(l.nfse);
          const nfcom = paraNumero(l.nfcom);
          const temQuebra = !Number.isNaN(nfse) || !Number.isNaN(nfcom);
          return {
            clientId: l.clientId,
            percentual: paraNumero(l.percentual),
            valorVigente: paraNumero(l.valor),
            valorVigentePorNatureza: temQuebra ? { nfse: Number.isNaN(nfse) ? 0 : nfse, nfcom: Number.isNaN(nfcom) ? 0 : nfcom } : null,
          };
        }),
        ...(divergencia && recalcular ? { recalcularCotas: true } : {}),
      });
      esquecerRateio();
      onDeclarado();
    } catch (e: any) {
      const p = lerProblemasDaDeclaracao(e);
      const d = lerCotasDivergentes(e);
      if (p) setProblemas(p);
      else if (d) { setDivergencia(d); setRecalcular(false); }
      else setErroSimples(typeof e?.response?.data?.message === 'string' ? e.response.data.message : (e?.response?.data?.mensagem || e?.message || 'Erro ao declarar'));
    } finally {
      setEnviando(false);
    }
  };

  const bloqueado = enviando || !competencia || !observacao.trim() || (!!divergencia && !recalcular);
  const inputCls = 'w-full px-2 py-1.5 bg-slate-950 border border-slate-700 rounded-md text-sm text-white tabular-nums text-right';

  return (
    // Formulário: não fecha ao clicar fora nem no Esc.
    <CascaDeModal
      largura='lg'
      titulo='Declarar competência passada'
      subtitulo={`matriz #${rateio.matrizId} · grava uma versão que começa no mês informado`}
      onClose={onClose}
      topo={<p className='px-5 pt-3 text-sm text-slate-400'>Para um mês que já passou, a divisão é declarada, não medida. Informe o pool daquele mês, as fatias em reais e de onde saíram os números.</p>}
      corpoClassName='p-5 space-y-4'
      rodape={
        <>
          <span className='text-xs text-slate-500'>Grava uma versão nova. Não altera faturas já emitidas.</span>
          <div className='ml-auto flex gap-2'>
            <button type='button' onClick={onClose} className='px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-white'>Cancelar</button>
            <button type='button' onClick={declarar} disabled={bloqueado} className='px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-sm font-medium text-white disabled:opacity-50'>
              {enviando ? 'Declarando…' : 'Declarar'}
            </button>
          </div>
        </>
      }
    >
      {problemas && (
        <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger}`} role='alert'>
          <p className={`font-medium ${TONE_TEXT.danger}`}>A declaração voltou com {problemas.length} problema{problemas.length === 1 ? '' : 's'}. Nada foi gravado.</p>
          <ul className='mt-1 list-disc pl-5 text-slate-200'>
            {problemas.map((p, i) => (
              <li key={i}>{p.problema} <span className='font-mono text-xs text-slate-400'>{p.campo}</span></li>
            ))}
          </ul>
        </div>
      )}
      {erroSimples && (
        <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} role='alert'>{erroSimples}</div>
      )}
      {divergencia && <AvisoDeCotasDivergentes divergencia={divergencia} recalcular={recalcular} onRecalcular={setRecalcular} />}

      <div className='grid grid-cols-3 gap-3'>
        <div>
          <label className='block text-xs text-slate-400 mb-1' htmlFor='cp-comp'>Competência de início *</label>
          <input id='cp-comp' type='month' value={competencia} onChange={(e) => setCompetencia(e.target.value)} className='w-full px-2 py-1.5 bg-slate-950 border border-slate-700 rounded-md text-sm text-white' />
        </div>
        <div>
          <label className='block text-xs text-slate-400 mb-1' htmlFor='cp-crit'>Critério *</label>
          <select id='cp-crit' value={criterio} onChange={(e) => setCriterio(e.target.value as CriterioDeRateio)} className='w-full px-2 py-1.5 bg-slate-950 border border-slate-700 rounded-md text-sm text-white'>
            {(Object.keys(TEXTO_DO_CRITERIO) as CriterioDeRateio[]).map((c) => <option key={c} value={c}>{TEXTO_DO_CRITERIO[c]}</option>)}
          </select>
        </div>
        <div>
          <label className='block text-xs text-slate-400 mb-1' htmlFor='cp-pool'>Pool da competência (R$) *</label>
          <input id='cp-pool' value={pool} onChange={(e) => setPool(e.target.value)} className={inputCls} />
        </div>
      </div>

      <table className='w-full text-sm'>
        <thead>
          <tr className='text-[10px] uppercase tracking-wider text-slate-500'>
            <th className='text-left font-medium py-1'>Empresa</th>
            <th className='font-medium py-1 w-24'>%</th>
            <th className='font-medium py-1 w-32'>Fatia (R$) *</th>
            <th className='font-medium py-1 w-28'>NFS-e</th>
            <th className='font-medium py-1 w-28'>NFCom</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={l.clientId} className='border-t border-central-borda'>
              <td className='py-1.5 pr-2 text-slate-200'>{nome(l.clientId)} <span className='font-mono text-xs text-slate-500'>#{l.clientId}</span></td>
              <td className='py-1.5 px-1'><input aria-label={`Percentual ${nome(l.clientId)}`} value={l.percentual} onChange={(e) => mudar(i, 'percentual', e.target.value)} className={inputCls} /></td>
              <td className='py-1.5 px-1'><input aria-label={`Fatia ${nome(l.clientId)}`} value={l.valor} onChange={(e) => mudar(i, 'valor', e.target.value)} className={inputCls} /></td>
              <td className='py-1.5 px-1'><input aria-label={`NFS-e ${nome(l.clientId)}`} value={l.nfse} onChange={(e) => mudar(i, 'nfse', e.target.value)} className={inputCls} /></td>
              <td className='py-1.5 px-1'><input aria-label={`NFCom ${nome(l.clientId)}`} value={l.nfcom} onChange={(e) => mudar(i, 'nfcom', e.target.value)} className={inputCls} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Conferência ao vivo, só informativa: a regra que vale é a do backend. */}
      <div className='flex justify-end gap-6 text-xs tabular-nums text-slate-400'>
        <span>Soma {soma.pct.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%</span>
        <span>Fatias R$ {paraTexto(soma.valor / 100)}</span>
        {poolCentavos != null && (
          <span className={soma.valor === poolCentavos ? TONE_TEXT.open : TONE_TEXT.warning}>
            Pool R$ {paraTexto(poolCentavos / 100)}{soma.valor === poolCentavos ? ' · fecha' : ` · diferença R$ ${paraTexto((poolCentavos - soma.valor) / 100)}`}
          </span>
        )}
      </div>

      <div>
        <label className='block text-xs text-slate-400 mb-1' htmlFor='cp-obs'>Observação * <span className='text-slate-500'>(de onde saíram os números)</span></label>
        <textarea id='cp-obs' rows={3} value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder='Ex.: valores das faturas do mês, conferidos contra o boleto pago' className='w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white' />
      </div>
    </CascaDeModal>
  );
}
